import { useCallback, useEffect, useRef } from 'react';

export function useRecordingWaveform(drivingListening: boolean) {
  const waveTargetRef = useRef(0);
  const waveVisualRef = useRef(0);
  const waveFrameRef = useRef(0);
  const listeningRef = useRef(drivingListening);
  const waveBarsContainerRef = useRef<HTMLDivElement | null>(null);

  const paintWaveform = useCallback(() => {
    const container = waveBarsContainerRef.current;
    if (!container) {
      waveFrameRef.current = 0;
      return;
    }

    const bars = Array.from(container.querySelectorAll('.recording-wave-bar')) as HTMLElement[];
    if (bars.length === 0) {
      waveFrameRef.current = 0;
      return;
    }

    const listening = listeningRef.current;
    waveVisualRef.current += (waveTargetRef.current - waveVisualRef.current) * 0.28;
    const now = performance.now();
    const mid = (bars.length - 1) / 2;

    bars.forEach((bar, index) => {
      const centerDistance = Math.abs(index - mid) / (mid + 1);
      const centerWeight = Math.exp(-(centerDistance * centerDistance) / 0.19);
      const driftA = (Math.sin(now / 220 + index * 0.34) + 1) / 2;
      const driftB = (Math.sin(now / 360 - index * 0.21) + 1) / 2;
      const idle = 0.08 + centerWeight * 0.08 + driftB * 0.025;
      const voice = waveVisualRef.current * (0.18 + centerWeight * 0.9) * (0.45 + driftA * 0.55);
      const scale = Math.min(0.92, idle + voice);
      bar.style.setProperty('--wave-scale', scale.toFixed(3));
      bar.style.opacity = listening ? (0.36 + scale * 0.56).toFixed(3) : '0';
    });

    if (
      listening ||
      waveVisualRef.current > 0.025 ||
      waveTargetRef.current > 0.025
    ) {
      waveFrameRef.current = requestAnimationFrame(paintWaveform);
    } else {
      waveFrameRef.current = 0;
    }
  }, []);

  const startWaveAnimation = useCallback(() => {
    if (!waveFrameRef.current) {
      waveFrameRef.current = requestAnimationFrame(paintWaveform);
    }
  }, [paintWaveform]);

  const setWaveLevel = useCallback((level: number) => {
    waveTargetRef.current = Math.min(Math.max(level, 0), 1);
    startWaveAnimation();
  }, [startWaveAnimation]);

  const resetWaveform = useCallback((immediate = false) => {
    waveTargetRef.current = 0;
    if (immediate) {
      waveVisualRef.current = 0;
      if (waveFrameRef.current) {
        cancelAnimationFrame(waveFrameRef.current);
        waveFrameRef.current = 0;
      }

      const container = waveBarsContainerRef.current;
      if (container) {
        (Array.from(container.querySelectorAll('.recording-wave-bar')) as HTMLElement[]).forEach((bar) => {
          bar.style.setProperty('--wave-scale', '0.18');
          bar.style.opacity = '0';
        });
      }
    }
  }, []);

  useEffect(() => {
    listeningRef.current = drivingListening;
    if (
      drivingListening ||
      waveVisualRef.current > 0.025 ||
      waveTargetRef.current > 0.025
    ) {
      startWaveAnimation();
    }
  }, [drivingListening, startWaveAnimation]);

  useEffect(() => () => {
    if (waveFrameRef.current) {
      cancelAnimationFrame(waveFrameRef.current);
    }
  }, []);

  return {
    waveBarsContainerRef,
    setWaveLevel,
    resetWaveform,
  };
}
